import { MediaCard, ScrollArea } from "@prismicio/editor-ui"
import { type ReactNode, type RefObject, useRef } from "react"

import { useMediaLibrary } from "./MediaLibraryContext"

export function MediaList() {
  const {
    onScrollToBottom,
    media,
    selectedMediaIds,
    removeMediaItemSelection,
    replaceMediaItemSelection,
    isUploading,
    uploadingFiles,
    isSearchingMedia,
    isMediaRevalidationInProgress
  } = useMediaLibrary()

  const scrollAreaRef = useRef<HTMLDivElement>(null)

  function onSelect(mediaId: string) {
    if (selectedMediaIds.has(mediaId)) return removeMediaItemSelection(mediaId)

    replaceMediaItemSelection(mediaId)
  }

  if (isUploading || isSearchingMedia || isMediaRevalidationInProgress) {
    scrollAreaRef.current?.scrollTo({
      top: 0,
      behavior: "instant"
    })
  }

  return (
    <MediaListScrollArea
      onScrollToBottom={onScrollToBottom}
      scrollAreaRef={scrollAreaRef}
    >
      {media.map((media) => (
        <MediaCard
          key={media.id}
          aria-label="media item"
          {...media}
          filenameHighlight={media.search_highlight?.filename}
          loading={isSearchingMedia}
          status={uploadingFiles.find((file) => file.id === media.id)?.status}
          selected={selectedMediaIds.has(media.id)}
          onCardClick={() => onSelect(media.id)}
          onCheckedChange={() => onSelect(media.id)}
        />
      ))}
    </MediaListScrollArea>
  )
}

interface MediaListScrollAreaProps {
  children: ReactNode
  scrollAreaRef?: RefObject<HTMLDivElement>
  onScrollToBottom?: () => void
}

export function MediaListScrollArea(props: MediaListScrollAreaProps) {
  const { children, scrollAreaRef, onScrollToBottom } = props

  const gap = 16
  const maxColumns = 4
  const mediaCardMinWidth = "250px"
  // Scale max width based on the container size so that we never have more than 4 columns
  const mediaCardMaxWidth = `calc((100% - ${
    gap * (maxColumns - 1)
  }px) / ${maxColumns})`

  return (
    <ScrollArea
      aria-label="Media list"
      ref={scrollAreaRef}
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, minmax(max(${mediaCardMinWidth}, ${mediaCardMaxWidth}), 1fr))`,
        gridTemplateRows: "1fr 1fr 1fr ",
        gap,
        paddingBottom: 16,
        position: "relative"
      }}
      onScrollToBottom={onScrollToBottom}
      threshold={500}
    >
      {children}
    </ScrollArea>
  )
}
